import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ErrorOccurrence as Occurrence,
  SystemAdminErrorsService,
  errorWhere,
} from '../../services/systemAdminErrorsService';
import { CopyableId, LogsLink, formatWhen, orgLabel, useOrgNames } from './errorsCommon';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="sys-field">
    <div className="sys-field__label">{label}</div>
    <div className="sys-field__value">{children}</div>
  </div>
);

// Shown in their own sections or the header, so left out of "Context".
const RECORD_SHOWN_ELSEWHERE = new Set(['err', 'level', 'event', 'time', 'timestamp', 'requestId', 'parentRequestId',
  'apigwRequestId', 'orgId', 'userId', 'activeUserId', 'method', 'path', 'job']);

/** The error part of a stored log line: name/code/message, other safe fields, stack, then any cause. */
const ErrorBlock: React.FC<{ err: Record<string, any>; depth?: number }> = ({ err, depth = 0 }) => {
  const { name, message, stack, cause, ...rest } = err;
  const extras = Object.entries(rest).filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object');
  return (
    <div className={depth ? 'sys-errors__cause' : undefined}>
      {depth > 0 && <div className="sys-panel__title mt-3 mb-1">Caused by</div>}
      <Field label="Error">{name || '—'}</Field>
      <Field label="Message"><span className="sys-errors__wrap">{message || '—'}</span></Field>
      {extras.map(([k, v]) => <Field key={k} label={k}><code>{String(v)}</code></Field>)}
      {typeof stack === 'string' && stack && (
        <details className="mt-2">
          <summary className="small">Stack trace</summary>
          <pre className="sys-errors__pre">{stack}</pre>
        </details>
      )}
      {cause && typeof cause === 'object' && depth < 3 && <ErrorBlock err={cause} depth={depth + 1} />}
    </div>
  );
};

/**
 * System Admin → Errors → one occurrence: when and for whom (ids only), the
 * request references, the stored log line (already scrubbed of personal data
 * when it was logged) and a CloudWatch link to the request's full log.
 */
const ErrorOccurrence: React.FC = () => {
  const { occurrenceId: idParam } = useParams<{ occurrenceId: string }>();
  const occurrenceId = Number(idParam);
  const orgNames = useOrgNames();

  const [occ, setOcc] = useState<Occurrence | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!Number.isInteger(occurrenceId) || occurrenceId <= 0) return;
    let cancelled = false;
    setError(null);
    SystemAdminErrorsService.fetchOccurrence(occurrenceId)
      .then(o => { if (!cancelled) setOcc(o); })
      .catch((err: any) => { if (!cancelled) setError(err?.response?.data?.error || 'Failed to load this occurrence.'); });
    return () => { cancelled = true; };
  }, [occurrenceId]);

  if (!Number.isInteger(occurrenceId) || occurrenceId <= 0) return <Navigate to="/system-admin/errors" replace />;

  const record = occ?.record ?? {};
  const context = Object.entries(record).filter(([k, v]) => !RECORD_SHOWN_ELSEWHERE.has(k) && v !== null && v !== undefined);

  return (
    <div className="sys-page">
      <div className="sys-detail__head">
        <Link to={occ ? `/system-admin/errors/${occ.groupId}` : '/system-admin/errors'} className="sys-back" title="Back to the error">
          <i className="fas fa-arrow-left" aria-hidden="true" />{occ ? 'Error group' : 'Errors'}
        </Link>
        <h1 className="sys-detail__name">
          <code>{typeof record.event === 'string' ? record.event : `Occurrence #${occurrenceId}`}</code>
        </h1>
      </div>

      <div className="sys-page__body">
        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : !occ ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading…</span></div>
          </div>
        ) : (
          <>
            <div className="sys-errors__cols mb-3">
              <div className="ivory-panel sys-panel sys-panel--pad">
                <h2 className="sys-panel__title">When and where</h2>
                <Field label="When">{formatWhen(occ.occurredAt)}</Field>
                <Field label="Where">{errorWhere(occ)}</Field>
                <Field label="Organisation">
                  {orgLabel(orgNames, occ.organisationId)}
                  {occ.organisationId !== null && <span className="sys-field__note">id {occ.organisationId}</span>}
                </Field>
                <Field label="User id">
                  {occ.userId ?? '—'}
                  {occ.activeUserId !== null && occ.activeUserId !== occ.userId && (
                    <span className="sys-field__note">acting as {occ.activeUserId} (PIN)</span>
                  )}
                </Field>
              </div>

              <div className="ivory-panel sys-panel sys-panel--pad">
                <h2 className="sys-panel__title">References</h2>
                <Field label="Request (user’s ref)"><CopyableId value={occ.requestId} /></Field>
                {occ.parentRequestId && (
                  <Field label="Started by request"><CopyableId value={occ.parentRequestId} title="The API request that invoked this worker" /></Field>
                )}
                <Field label="API Gateway id"><CopyableId value={occ.apigwRequestId} /></Field>
                <Field label="Logs"><LogsLink href={occ.logsLink} label="Open this request in CloudWatch" /></Field>
              </div>
            </div>

            <div className="ivory-panel sys-panel sys-panel--pad mb-3">
              <h2 className="sys-panel__title">The error</h2>
              {record.err && typeof record.err === 'object'
                ? <ErrorBlock err={record.err} />
                : <div className="text-muted small">No error object on this log line{typeof record.msg === 'string' ? ` — message: ${record.msg}` : ''}.</div>}
            </div>

            {context.length > 0 && (
              <div className="ivory-panel sys-panel sys-panel--pad mb-3">
                <h2 className="sys-panel__title">Context</h2>
                {context.map(([k, v]) => (
                  <Field key={k} label={k}>
                    {typeof v === 'object' ? <code className="sys-errors__wrap">{JSON.stringify(v)}</code> : <code>{String(v)}</code>}
                  </Field>
                ))}
              </div>
            )}

            <details className="ivory-panel sys-panel sys-panel--pad">
              <summary className="small">Stored log line (JSON)</summary>
              <pre className="sys-errors__pre mt-2">{JSON.stringify(record, null, 2)}</pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
};

export default ErrorOccurrence;
